import { Reducer, SyntheticEvent, useEffect, useReducer } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import Modal from '@components/styled-UI/Modal';

import { TextField } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import InputAdornment from '@mui/material/InputAdornment';

import NumberFormat from 'react-number-format';

import CalendarIcon from '@images/svg-icons/calendarIcon.svg';
import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import CloseIcon from '@images/svg-icons/closeIcon.svg';
import PreLoader from '@images/svg-icons/preLoader.svg';

import * as actions from '@redux-actions/';

import {
    SnycEyeExamsState,
    SyncEyeExams,
    SyncEyeExamsFormStatus,
    SyncEyeExamsPayload,
} from '@interfaces/myAppointmentInterfaces';

import { syncMyEyeExams } from '@services/myAppointmentServices';

import {
    capitalizeFirstLetter,
    getLabelFallback,
    getPartialPhoneNumber,
    isValidForm,
} from '@utils/utils';

import { replaceLabelPlaceholder } from '@utils/pages-utils/EyeExamsUtils';

import { getStringFromDate } from '@utils/dateUtils';

import { isValid, differenceInYears, parse, compareAsc } from 'date-fns';

import { cloneDeep } from 'lodash';

const initialFormData: SyncEyeExamsFormStatus<SyncEyeExams> = {
    firstname: {
        value: '',
        focus: false,
        success: false,
        error: '',
    },
    lastname: {
        value: '',
        focus: false,
        success: false,
        error: '',
    },
    dob: {
        value: '',
        focus: false,
        success: false,
        error: '',
    },
    phone: {
        value: undefined,
        focus: false,
        success: false,
        error: '',
    },
};

/** The function below returns the input date in MM/dd/yyyy format */

export const getFormattedDate = (input: string): string => {
    let clearDate = input.replace(/\//g, '');
    let formattedDate = '';

    if (clearDate.length === 1) {
        formattedDate = clearDate;
    }
    if (clearDate.length >= 2 && clearDate.length < 4) {
        formattedDate = clearDate.slice(0, 2) + '/' + clearDate.slice(2, 3);
    }
    if (clearDate.length >= 4 && clearDate.length < 5) {
        formattedDate = clearDate.slice(0, 2) + '/' + clearDate.slice(2, 4) + '/';
    }
    if (clearDate.length >= 5) {
        formattedDate =
            clearDate.slice(0, 2) + '/' + clearDate.slice(2, 4) + '/' + clearDate.slice(4, 8);
    }
    return formattedDate;
};

const EyeExamsOverview = (): JSX.Element => {
    const [syncEyeExamsState, setSyncEyeExamsState] = useReducer<
        Reducer<SnycEyeExamsState, Partial<SnycEyeExamsState>>
    >((state, newState) => ({ ...state, ...newState }), {
        isOpenSyncModal: false,
        isOpenDatePicker: false,
        formData: initialFormData,
        counter: 7,
        isLongSubmitting: false,
    });

    const dispatch = useDispatch();

    const isMobile = useSelector((state: RootState) => state.common.isMobile);
    const config = useSelector((state: RootState) => state.common.config);
    const syncMyEyeExamsRequestStatus = useSelector(
        (state: RootState) => state.myAppointment.syncMyEyeExamsRequestStatus
    );

    useEffect(() => {
        syncMyEyeExamsRequestStatus === 'SUCCESS' ? resetForm() : null;
    }, [syncMyEyeExamsRequestStatus]);

    useEffect(() => {
        const timer =
            syncEyeExamsState.counter > 0 &&
            syncMyEyeExamsRequestStatus === 'LOADING' &&
            setInterval(
                () => setSyncEyeExamsState({ counter: syncEyeExamsState.counter - 1 }),
                1000
            );
        syncEyeExamsState.counter === 0 && setSyncEyeExamsState({ isLongSubmitting: true });
        return () => clearInterval(timer);
    }, [syncEyeExamsState.counter, syncMyEyeExamsRequestStatus]);

    /** The belows function redirects the user to the find a store page*/

    const goToFindStore = () => {
        window.open(
            config.callFromComponentToTO.myAppointments.eyeExams.scheduleEyeExam,
            '_parent'
        );
    };

    /** The belows function checks if the input date is valid */

    const checkIfValidDate = (inputDate: Date, formattedDate: string) => {
        isValid(inputDate) &&
        compareAsc(inputDate, new Date('01/01/1900')) !== -1 &&
        compareAsc(inputDate, new Date()) !== 1 &&
        differenceInYears(new Date(), new Date(inputDate)) >= 18
            ? setSyncEyeExamsState({
                  formData: {
                      ...syncEyeExamsState.formData,
                      dob: {
                          success: true,
                          focus: false,
                          value: formattedDate,
                          error: '',
                      },
                  },
              })
            : setSyncEyeExamsState({
                  formData: {
                      ...syncEyeExamsState.formData,
                      dob: {
                          success: false,
                          focus: false,
                          value: formattedDate,
                          error:
                              differenceInYears(new Date(), new Date(inputDate)) < 18
                                  ? getLabelFallback(
                                        'MY_APPOINTMENT_EYE_EXAMS_INVALID_DATE_OF_BIRTH'
                                    )
                                  : getLabelFallback('INVALID_DATE_MESSAGE'),
                      },
                  },
              });
    };
    /** The belows function checks if the phone number has fourteen digits */

    const validatePhoneNumber = (phoneNumber: string) => {
        const onlyNumbers = phoneNumber.replace(/\D/g, '');
        return onlyNumbers.length < 10
            ? setSyncEyeExamsState({
                  formData: {
                      ...syncEyeExamsState.formData,
                      phone: {
                          success: false,
                          focus: false,
                          value: phoneNumber,
                          error: getLabelFallback('MY_APPOINTMENT_EYE_EXAMS_INVALID_PHONE_NUMBER'),
                      },
                  },
              })
            : setSyncEyeExamsState({
                  formData: {
                      ...syncEyeExamsState.formData,
                      phone: {
                          success: true,
                          focus: false,
                          value: phoneNumber,
                          error: '',
                      },
                  },
              });
    };

    /** The function below handles the onBlur, onChange e onFocus events for the form validation */

    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const event = e.target as HTMLInputElement;
        if (
            event.name === 'dob' &&
            e.type === 'blur' &&
            syncEyeExamsState.formData.dob.hasOwnProperty('focus')
        ) {
            const parsedDate = parse(event.value, 'P', new Date());
            return checkIfValidDate(parsedDate, event.value);
        }

        if (
            event.name === 'phone' &&
            e.type === 'blur' &&
            syncEyeExamsState.formData.dob.hasOwnProperty('focus')
        ) {
            return validatePhoneNumber(event.value);
        }

        if (
            ['firstname', 'lastname'].includes(event.name) &&
            e.type == 'blur' &&
            event.value.length &&
            /\d/.test(event.value)
        ) {
            return setSyncEyeExamsState({
                formData: {
                    ...syncEyeExamsState.formData,
                    [event.name]: {
                        value: event.value,
                        error: getLabelFallback(
                            'MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_SYNC_MODAL_FORM_FIELD_INVALID_FIELD'
                        ),
                        success: false,
                        focus: false,
                    },
                },
            });
        }

        event.value && syncEyeExamsState.formData[event.name].hasOwnProperty('focus')
            ? setSyncEyeExamsState({
                  formData: {
                      ...syncEyeExamsState.formData,
                      [event.name]: {
                          value: event.value,
                          error: '',
                          success: e.type === 'blur' ? true : e.type === 'change' ? false : false,
                          focus: e.type === 'blur' ? false : e.type === 'change' ? true : true,
                      },
                  },
              })
            : setSyncEyeExamsState({
                  formData: {
                      ...syncEyeExamsState.formData,
                      [event.name]: {
                          success: false,
                          focus: false,
                          value: '',
                          error:
                              e.type === 'blur' &&
                              replaceLabelPlaceholder(
                                  'FIELD_CANNOT_BE_EMPTY_MESSAGE',
                                  '{FORM_FIELD}',
                                  getLabelFallback(
                                      `MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_SYNC_MODAL_FORM_FIELD_${event.name.toUpperCase()}_PLACEHOLDER_LABEL`
                                  )
                              ),
                      },
                  },
              });
    };

    /** The function below resets all form values and states */

    const resetForm = () => {
        setSyncEyeExamsState({
            isOpenSyncModal: false,
            isLongSubmitting: false,
            counter: 7,
            formData: initialFormData,
        });

        dispatch(actions.syncMyEyeExamsSuccess());
    };

    /** The function below reset the form field value */

    const resetField = (field: keyof SyncEyeExams) => {
        setSyncEyeExamsState({
            formData: {
                ...syncEyeExamsState.formData,
                [field]: {
                    value: '',
                },
            },
        });
    };

    /** The function below checks if all form fields have a value; if true nothing happening otherwise are setted the empty error messages for all the empty fields */

    const setFormFieldErrorMessages = (formData: SyncEyeExamsFormStatus<SyncEyeExams>) => {
        const formDataCopy = cloneDeep(formData);
        Object.keys(formDataCopy).forEach(key => {
            if (!formDataCopy[key].value) {
                formDataCopy[key] = {
                    success: false,
                    error: replaceLabelPlaceholder(
                        'FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback(
                            `MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_SYNC_MODAL_FORM_FIELD_${key.toUpperCase()}_PLACEHOLDER_LABEL`
                        )
                    ),
                    focus: false,
                    value: '',
                };
            }
        });

        setSyncEyeExamsState({
            formData: formDataCopy,
        });
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const { firstname, lastname, dob, phone } = syncEyeExamsState.formData;
        setFormFieldErrorMessages(syncEyeExamsState.formData);

        if (!isValidForm(syncEyeExamsState.formData)) {
            dispatch(actions.syncMyEyeExamsPending());
            const payload: SyncEyeExamsPayload = {
                storeId: config.storeId.toString(),
                catalogId: config.catalogId.toString(),
                langId: config.langId.toString(),
                logonId: config.userEmail,
                email1: config.userEmail,
                URL: 'AddEyeExam',
                formName: 'AddEyeExamSubmitForm',
                isFirstTimeOpenEyeExamHistoryPage:
                    config.isFirstTimeOpenEyeExamHistoryPage.toString(),
                [`isFirstTimeOpenEyeExamHistoryPage_${config.storeId}_r_1`]:
                    config.isFirstTimeOpenEyeExamHistoryPage.toString(),
                firstName: firstname.value,
                lastName: lastname.value,
                dateOfBirth: getStringFromDate(new Date(dob.value), 'yyyy-MM-dd'),
                phone1_1: getPartialPhoneNumber(phone.value, 0, 3),
                phone1_2: getPartialPhoneNumber(phone.value, 3, 6),
                phone1_3: getPartialPhoneNumber(phone.value, 6, 10),
                phone1: phone.value.replace(/\s/g, ''),
                consentCheckbox: 'on',
                fromPage: 'eyeExamHistory',
            };

            dispatch(syncMyEyeExams(payload, config.endPointStatusExams, config.difKeyStatusAppointments, config.authEncoded));
        }
    };

    return (
        <>
            <div className="eye-exams-overview">
                <div className="eye-exams-overview-content">
                    <div
                        role="region"
                        aria-label="eye exams overview left title"
                        className="eye-exams-overview-content-title"
                    >
                        {getLabelFallback('MY_APPOINTMENTS_EYE_EXAMS_FIRST_OVERVIEW_TITLE')}
                    </div>
                    <div
                        role="region"
                        aria-label="eye exams overview left description"
                        className="eye-exams-overview-content-message"
                    >
                        {getLabelFallback('MY_APPOINTMENTS_EYE_EXAMS_FIRST_OVERVIEW_MESSAGE')}
                    </div>
                    <button
                        role="region"
                        aria-label="eye exams overview left button"
                        data-element-id="BookAnAppointment_CTA"
                        data-description="Book"
                        className="primary-button button-size-medium"
                        onClick={goToFindStore}
                    >
                        {getLabelFallback('MY_APPOINTMENTS_EYE_EXAMS_FIRST_OVERVIEW_CTA_LABEL')}
                    </button>
                </div>
                <div className="eye-exams-overview-content">
                    <span
                        role="region"
                        aria-label="eye exams overview right title"
                        className="eye-exams-overview-content-title"
                    >
                        {getLabelFallback('MY_APPOINTMENTS_EYE_EXAMS_SECOND_OVERVIEW_TITLE')}
                    </span>
                    <div
                        role="region"
                        aria-label="eye exams overview right description"
                        className="eye-exams-overview-content-message"
                    >
                        {getLabelFallback('MY_APPOINTMENTS_EYE_EXAMS_SECOND_OVERVIEW_MESSAGE')}
                    </div>
                    <button
                        role="region"
                        aria-label="eye exams overview right button"
                        data-element-id="SyncAppointment_CTA"
                        data-description="Sync"
                        className="primary-button-negative button-size-medium"
                        onClick={() => setSyncEyeExamsState({ isOpenSyncModal: true })}
                    >
                        {getLabelFallback('MY_APPOINTMENTS_EYE_EXAMS_SECOND_OVERVIEW_CTA_LABEL')}
                    </button>
                </div>
            </div>
            <Modal
                isOpen={syncEyeExamsState.isOpenSyncModal}
                close={resetForm}
                isCentered
                showCloseIcon
                customStyle={isMobile ? 'eye-exams-form-modal-sm' : 'eye-exams-modal'}
            >
                <div role="region" aria-label="Eye exams sync modal" className="eye-exams-sync">
                    <div className="title">
                        {getLabelFallback(
                            syncEyeExamsState.isLongSubmitting
                                ? 'MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_LONG_SYNC_MODAL_TITLE'
                                : 'MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_SYNC_MODAL_TITLE'
                        )}
                    </div>
                    <div className="subtitle">
                        {getLabelFallback(
                            syncEyeExamsState.isLongSubmitting
                                ? 'MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_LONG_SYNC_MODAL_SUBTITLE'
                                : 'MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_SYNC_MODAL_SUBTITLE'
                        )}
                    </div>
                    {syncEyeExamsState.isLongSubmitting ? (
                        <div className="loader-container">
                            <PreLoader viewBox="0 0 24 24" />
                        </div>
                    ) : (
                        <form autoComplete="off" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <TextField
                                    className={`${
                                        syncEyeExamsState.formData.firstname.error
                                            ? 'form-field-error'
                                            : syncEyeExamsState.formData.firstname.success
                                            ? 'form-field-success'
                                            : ''
                                    }`}
                                    onChange={handleInputEvent}
                                    onFocus={handleInputEvent}
                                    onBlur={handleInputEvent}
                                    helperText={syncEyeExamsState.formData.firstname.error}
                                    name="firstname"
                                    value={syncEyeExamsState.formData.firstname.value}
                                    variant="outlined"
                                    label={capitalizeFirstLetter(
                                        getLabelFallback(
                                            'MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_SYNC_MODAL_FORM_FIELD_FIRSTNAME_LABEL'
                                        )
                                    )}
                                    InputProps={{
                                        endAdornment: syncEyeExamsState.formData.firstname.focus ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <CloseIcon
                                                    className="cursor-pointer"
                                                    onMouseDown={() => resetField('firstname')}
                                                />
                                            </InputAdornment>
                                        ) : syncEyeExamsState.formData.firstname.error ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <ErrorIcon />
                                            </InputAdornment>
                                        ) : syncEyeExamsState.formData.firstname.success ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <SuccessIcon />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                />
                                <TextField
                                    className={`${
                                        syncEyeExamsState.formData.lastname.error
                                            ? 'form-field-error'
                                            : syncEyeExamsState.formData.lastname.success
                                            ? 'form-field-success'
                                            : ''
                                    }`}
                                    onChange={handleInputEvent}
                                    onFocus={handleInputEvent}
                                    onBlur={handleInputEvent}
                                    helperText={syncEyeExamsState.formData.lastname.error}
                                    name="lastname"
                                    variant="outlined"
                                    label={capitalizeFirstLetter(
                                        getLabelFallback(
                                            'MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_SYNC_MODAL_FORM_FIELD_LASTNAME_LABEL'
                                        )
                                    )}
                                    value={syncEyeExamsState.formData.lastname.value}
                                    InputProps={{
                                        endAdornment: syncEyeExamsState.formData.lastname.focus ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <CloseIcon
                                                    className="cursor-pointer"
                                                    onMouseDown={() => resetField('lastname')}
                                                />
                                            </InputAdornment>
                                        ) : syncEyeExamsState.formData.lastname.error ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <ErrorIcon />
                                            </InputAdornment>
                                        ) : syncEyeExamsState.formData.lastname.success ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <SuccessIcon />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                />
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DesktopDatePicker
                                        minDate={new Date('01/01/1900')}
                                        disableFuture
                                        className={`${
                                            syncEyeExamsState.formData.dob.error
                                                ? 'form-field-error'
                                                : syncEyeExamsState.formData.dob.success
                                                ? 'form-field-success'
                                                : ''
                                        }`}
                                        label={capitalizeFirstLetter(
                                            getLabelFallback(
                                                'MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_SYNC_MODAL_FORM_FIELD_DOB_LABEL'
                                            )
                                        )}
                                        open={syncEyeExamsState.isOpenDatePicker}
                                        onClose={() =>
                                            setSyncEyeExamsState({ isOpenDatePicker: false })
                                        }
                                        onAccept={date =>
                                            checkIfValidDate(
                                                date,
                                                getStringFromDate(date, 'MM/dd/yyyy')
                                            )
                                        }
                                        openTo="year"
                                        views={['year', 'month', 'day']}
                                        inputFormat="MM/dd/yyyy"
                                        value={syncEyeExamsState.formData.dob.value}
                                        onChange={() => {}}
                                        renderInput={params => (
                                            <TextField
                                                helperText={syncEyeExamsState.formData.dob.error}
                                                name="dob"
                                                variant="outlined"
                                                onBlur={handleInputEvent}
                                                onFocus={handleInputEvent}
                                                onChange={(e: SyntheticEvent) =>
                                                    setSyncEyeExamsState({
                                                        formData: {
                                                            ...syncEyeExamsState.formData,
                                                            dob: {
                                                                value: /^[\/0-9\b]+$/.test(
                                                                    (e.target as HTMLInputElement)
                                                                        .value
                                                                )
                                                                    ? getFormattedDate(
                                                                          (
                                                                              e.target as HTMLInputElement
                                                                          ).value
                                                                      )
                                                                    : (e.target as HTMLInputElement)
                                                                          .value
                                                                    ? syncEyeExamsState.formData.dob
                                                                          .value
                                                                    : '',
                                                                error: '',
                                                                success: false,
                                                                focus: true,
                                                            },
                                                        },
                                                    })
                                                }
                                                value={syncEyeExamsState.formData.dob.value}
                                                {...params}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    value: syncEyeExamsState.formData.dob.value,
                                                }}
                                                InputProps={{
                                                    endAdornment:
                                                        syncEyeExamsState.formData.dob.focus &&
                                                        syncEyeExamsState.formData.dob.value ? (
                                                            <InputAdornment
                                                                className="large"
                                                                position="end"
                                                            >
                                                                <CloseIcon
                                                                    className="cursor-pointer"
                                                                    onMouseDown={() =>
                                                                        resetField('dob')
                                                                    }
                                                                />
                                                                <CalendarIcon
                                                                    className="input-field-icon"
                                                                    onClick={() =>
                                                                        setSyncEyeExamsState({
                                                                            isOpenDatePicker: true,
                                                                        })
                                                                    }
                                                                />
                                                            </InputAdornment>
                                                        ) : syncEyeExamsState.formData.dob.error ? (
                                                            <InputAdornment
                                                                className="large"
                                                                position="end"
                                                            >
                                                                <ErrorIcon />
                                                                <CalendarIcon
                                                                    className="input-field-icon"
                                                                    onClick={() =>
                                                                        setSyncEyeExamsState({
                                                                            isOpenDatePicker: true,
                                                                        })
                                                                    }
                                                                />
                                                            </InputAdornment>
                                                        ) : syncEyeExamsState.formData.dob
                                                              .success ? (
                                                            <InputAdornment
                                                                className="large"
                                                                position="end"
                                                            >
                                                                <SuccessIcon />
                                                                <CalendarIcon
                                                                    className="input-field-icon"
                                                                    onClick={() =>
                                                                        setSyncEyeExamsState({
                                                                            isOpenDatePicker: true,
                                                                        })
                                                                    }
                                                                />
                                                            </InputAdornment>
                                                        ) : (
                                                            <CalendarIcon
                                                                className="input-field-icon medium"
                                                                onClick={() =>
                                                                    setSyncEyeExamsState({
                                                                        isOpenDatePicker: true,
                                                                    })
                                                                }
                                                            />
                                                        ),
                                                }}
                                                error={Boolean(
                                                    syncEyeExamsState.formData.dob.error
                                                )}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>

                                <NumberFormat
                                    format={'(###) ###-####'}
                                    customInput={TextField}
                                    className={`${
                                        syncEyeExamsState.formData.phone.error
                                            ? 'form-field-error'
                                            : syncEyeExamsState.formData.phone.success
                                            ? 'form-field-success'
                                            : ''
                                    }`}
                                    name="phone"
                                    helperText={syncEyeExamsState.formData.phone.error}
                                    variant="outlined"
                                    label={capitalizeFirstLetter(
                                        getLabelFallback(
                                            'MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_SYNC_MODAL_FORM_FIELD_PHONE_LABEL'
                                        )
                                    )}
                                    onChange={handleInputEvent}
                                    onFocus={handleInputEvent}
                                    onBlur={handleInputEvent}
                                    value={syncEyeExamsState.formData.phone.value}
                                    InputProps={{
                                        endAdornment: syncEyeExamsState.formData.phone.focus ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <CloseIcon
                                                    className="cursor-pointer"
                                                    onMouseDown={() => resetField('phone')}
                                                />
                                            </InputAdornment>
                                        ) : syncEyeExamsState.formData.phone.error ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <ErrorIcon />
                                            </InputAdornment>
                                        ) : syncEyeExamsState.formData.phone.success ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <SuccessIcon />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                />
                            </div>

                            {syncMyEyeExamsRequestStatus === 'ERROR' && (
                                <div className="generic-error-message">
                                    <ErrorIcon />
                                    {getLabelFallback(
                                        'MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_SYNC_MODAL_GENERIC_ERROR'
                                    )}
                                </div>
                            )}

                            <div
                                className={`agreement-message ${
                                    syncMyEyeExamsRequestStatus === 'ERROR' && ' error'
                                }`}
                            >
                                {getLabelFallback(
                                    'MYAPPOINTMENT_EYE_EXAMS_OVERVIEW_SYNC_MODAL_AGREE_MESSAGE'
                                )}
                            </div>

                            <div className="button-container">
                                <button
                                    type="submit"
                                    className={`primary-button button-size-medium ${syncMyEyeExamsRequestStatus === 'LOADING' ? 'loading' : ''
                                        }`}
                                    data-element-id="OEEOverlay_Synch"
                                    data-description="Submit"
                                >
                                    {getLabelFallback('SUBMIT_CTA_LABEL')}
                                    {syncMyEyeExamsRequestStatus === 'LOADING' && <PreLoader />}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </Modal>
        </>
    );
};
export default EyeExamsOverview;
